import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ImportStep } from './types'
import { useUploadMutation, isUploadAbortError } from './api/uploadMutation'
import { useUploadStatusPooling } from '@/lib/api/company/fileProcessing/status/useUploadStatusPooling'
import { useProcessedFilesReadModel } from '@/modules/imports/hooks/useProcessedFilesReadModel'
import { ImportedFileLogsSubmitDto } from '@/modules/imports/model/imports.submit.dto'
import { useImportMutation } from './api/importMutation'
import { useImportStatusPooling } from '@/lib/api/config/importTask/status/useImportStatusPooling'
import { TaskModel } from '@/app/models/tasks/taskModel'
import { TaskType } from '@/app/constants/taskType'
import { useTask } from '@/modules/shared/context/TaskContext'
import { invalidateAfterImport } from '@/modules/imports/utils/invalidateAfterImport'

export type ImportSummarySnapshot = {
  selectedDrivers: number
  totalDrivers: number
  selectedVehicles: number
  totalVehicles: number
  /** Registration numbers of vehicles visible after dateFrom filtering */
  filteredVehicleRegistrationNumbers: string[]
  /** Registration numbers of vehicles selected by user (subset of filteredVehicleRegistrationNumbers) */
  selectedVehicleRegistrationNumbers: string[]
  /** Card numbers of drivers selected by user */
  selectedDriverCardNumbers: string[]
}

type Props = {
  companyId: string
  handleClose?: () => void
  onSuccess?: () => void
}

export const countAllItems = (dto: ImportedFileLogsSubmitDto): number => {
  return (
    dto.existingDrivers.length +
    dto.ignoredDrivers.length +
    dto.newDrivers.length +
    dto.existingVehicles.length +
    dto.ignoredVehicles.length +
    dto.newVehicles.length
  )
}

export const useImportFlow = ({ companyId, handleClose, onSuccess }: Props) => {
  const queryClient = useQueryClient()
  const { addTask } = useTask()
  const [step, setStep] = React.useState<ImportStep>('user:uploadFiles')
  const uploadTaskId = React.useRef<string | null>(null)
  const processedFileId = React.useRef<string | null>(null)
  const importTaskId = React.useRef<string | null>(null)
  const filesCount = React.useRef<number>(0)
  // const uploadedFilesCount = React.useRef<number>(0)
  const logsCount = React.useRef<number>(0)
  const summarySnapshotRef = React.useRef<ImportSummarySnapshot | null>(null)
  const [uploadedFilesCount, setUploadedFilesCount] = React.useState<number>(0)
  const uploadAbortedRef = React.useRef(false)
  const uploadAbortControllerRef = React.useRef<AbortController | null>(null)

  // setTimeout(() => setStep('user:confirmSummary'), 9000)
  // 4.fetching:uploadDetails -> 5. user:mapAndSelect
  //   const queryImportDetails = useProcessedFilesReadModel({
  //     companyId,
  //     fileId: processedFileId.current,
  //     disabled: step !== 'fetching:uploadDetails',
  //   })
  //   React.useEffect(() => {
  //     if (queryImportDetails.data) {
  //       setStep('user:confirmSummary')
  //     }
  //   }, [queryImportDetails.data])

  // polling:importStatus -> 4.fetching:importDetails
  const queryImportStatus = useImportStatusPooling({
    companyId,
    taskId: uploadTaskId.current,
    fileId: processedFileId.current,
    disabled: step !== 'polling:importStatus',
    onDone: (task: TaskModel) => {
      invalidateAfterImport(queryClient)
      setStep('user:confirmSummary')
    }, // @TODO: Add fetch historical data
    // onDone: (task: TaskModel) => setStep('fetching:importDetails'), // save details?
    intervalMs: 500,
    importTaskId: importTaskId.current,
  })

  // Podsumowanie pokazuje stan z bazy po zapisie, nie zaznaczenie użytkownika.
  const queryImportResult = useProcessedFilesReadModel({
    companyId,
    fileId: processedFileId.current,
    disabled: step !== 'user:confirmSummary',
    afterSave: true,
  })

  // -> polling:importStatus
  const importMutation = useImportMutation({
    companyId,
    onSuccess: (taskId: string) => {
      importTaskId.current = taskId
      // Hand the save task to the GLOBAL task store as well. The polling below
      // only lives as long as this popup, so without this the user sees nothing
      // once they close it or navigate away - while the fileprocessor is still
      // writing activities. GlobalSaveImportProgress picks it up from there and
      // keeps a banner on screen until the save really finishes.
      addTask(taskId, TaskType.SaveImport, null)
      setStep('polling:importStatus')
    },
    uploadTaskId: uploadTaskId.current,
    processedFileId: processedFileId.current,
  })

  // 4.fetching:uploadDetails -> 5. user:mapAndSelect
  const queryUploadDetails = useProcessedFilesReadModel({
    companyId,
    fileId: processedFileId.current,
    disabled: step !== 'fetching:uploadDetails',
  })
  React.useEffect(() => {
    if (queryUploadDetails.data) {
      setStep('user:mapAndSelect')
    }
  }, [queryUploadDetails.data])

  // 3. polling:uploadStatus -> 4.fetching:uploadDetails
  useUploadStatusPooling({
    companyId,
    taskId: uploadTaskId.current,
    fileId: processedFileId.current,
    disabled: step !== 'polling:uploadStatus',
    onDone: () => setStep('fetching:uploadDetails'),
    intervalMs: 2000,
  })

  // 1.user:uploadFiles -> 2.sending:uploadFiles
  // 2.sending:uploadFiles -> 3. polling:uploadStatus
  const uploadMutation = useUploadMutation({
    // uploadAbortedRef,
    companyId,
    setUploadedFilesCount: (num) => {
      setUploadedFilesCount(num)
    },
    onSuccess: (fileId: string, taskId: string) => {
      if (uploadAbortedRef.current) return
      processedFileId.current = fileId
      uploadTaskId.current = taskId

      setStep('polling:uploadStatus')
    },
  })

  // Hide abort error in UI (user chose to cancel, not a real failure)
  React.useEffect(() => {
    if (uploadMutation.isError && uploadMutation.error && isUploadAbortError(uploadMutation.error)) {
      uploadMutation.reset()
    }
  }, [uploadMutation.isError, uploadMutation.error, uploadMutation])

  // 1.user:uploadFiles -> 2.sending:uploadFiles
  const handleUploadFiles = (files: File[]) => {
    filesCount.current = files.length
    setStep('sending:uploadFiles')
    const controller = new AbortController()
    uploadAbortControllerRef.current = controller
    uploadMutation
      .mutateAsync({ files, signal: controller.signal })
      .catch((err: unknown) => {
        if (!isUploadAbortError(err)) throw err
      })
  }

  const handleImport = (logs: ImportedFileLogsSubmitDto, snapshot?: ImportSummarySnapshot) => {
    logsCount.current = countAllItems(logs)
    if (snapshot) summarySnapshotRef.current = snapshot
    setStep('sending:importData')
    importMutation.mutateAsync(logs)
  }

  const handleRestart = () => {
    uploadAbortedRef.current = false
    uploadAbortControllerRef.current = null
    // restart
    uploadTaskId.current = null
    processedFileId.current = null
    importTaskId.current = null
    filesCount.current = 0
    // uploadedFilesCount.current = 0
    setUploadedFilesCount(0)
    logsCount.current = 0
    summarySnapshotRef.current = null
    // restart step
    setStep('user:uploadFiles')
    // close
    if (handleClose) handleClose()
  }

  const handleAbortAndReset = () => {
    // stop any inflight upload
    uploadAbortedRef.current = true
    uploadAbortControllerRef.current?.abort()

    // stop showing pending states & errors
    uploadMutation.reset()
    importMutation.reset()

    // ensure we are not in import polling step anymore
    setStep('user:uploadFiles')

    // full cleanup
    handleRestart()
  }

  const handleFinish = () => {
    if (onSuccess) onSuccess()
    handleRestart()
  }

  // Pliki z danymi są zapisane już po odczycie, bez kroku „Zaimportuj”.
  const handleDone = () => {
    invalidateAfterImport(queryClient)
    handleFinish()
  }

  return {
    importTask: queryImportStatus.data,
    uploadDetails: queryUploadDetails.data,
    importResult: queryImportResult.data,
    isImportResultPending: queryImportResult.isPending,
    step,
    filesCount: filesCount.current,
    uploadedFilesCount: uploadedFilesCount,
    logsCount: logsCount.current,
    summarySnapshot: summarySnapshotRef.current,
    handleUploadFiles,
    handleImport,
    handleFinish,
    handleDone,
    handleRestart,
    handleAbortAndReset,
  }
}
